import { useMountEffect, usePrevious } from '@timelyapp/react';
import { isEqual, mapValues, omit } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notNull, pick } from 'utils/typesafe';
import * as z from 'zod';

export type QueryStateFromSchema<T extends Record<string, z.ZodType<unknown>>> = {
  [Key in keyof T]: z.infer<T[Key]>;
};

// Extend as neccessary
type ValidQueryStringTypes =
  | number
  | string
  | boolean
  | Array<string | null>
  | Array<number | null>
  | null
  | undefined;

export function useQueryParameterStateSync<
  TSchema extends Record<string, z.ZodType<ValidQueryStringTypes>>,
>(
  schema: TSchema,
  state: QueryStateFromSchema<TSchema>,
  updateState: (state: Partial<QueryStateFromSchema<TSchema>>) => void
) {
  const navigate = useNavigate();
  const location = useLocation();
  const { search, pathname } = location;

  const rawQueryParameters = useMemo(() => queryStringToDictionary(search), [search]);
  const parsedQueryParameter = useMemo(
    () => parseQueryParamsUsingSchema(rawQueryParameters, schema),
    [rawQueryParameters, schema]
  );

  const replaceLocationSearch = useCallback(() => {
    const allSchemaKeys = Object.keys(schema) as Array<keyof TSchema>;

    // Preserve query parameters that do not exist in the schema
    const foreignParameters = omit(rawQueryParameters, allSchemaKeys);

    // Only update query parameters from state keys that are defined in the schema (and not `never`)
    const validSchemaKeys = allSchemaKeys.filter(key => !(schema[key] instanceof z.ZodNever));
    const whitelistedState = pick(state, ...validSchemaKeys);
    const parametersFromState = toQueryParameterDictionary(whitelistedState);

    const newParameters = {
      ...parametersFromState,
      ...foreignParameters,
    };
    navigate(`${location.pathname}?${toQueryString(newParameters)}`, { replace: true });
  }, [schema, rawQueryParameters, state, navigate, location.pathname]);

  useMountEffect(() => {
    // Update state on mount
    const newState: Partial<QueryStateFromSchema<TSchema>> = {};
    Object.entries(parsedQueryParameter).forEach(([key, value]) => {
      if (value !== null && !isEqual(value, state[key])) {
        newState[key as keyof TSchema] = value;
      }
    });
    if (Object.keys(newState).length > 0) {
      updateState(newState);
    }

    // Update URL on mount
    replaceLocationSearch();
  });

  const previousState = usePrevious(state);
  const previousPathname = usePrevious(pathname);
  useEffect(() => {
    // Update URL when state changes
    if (!isEqual(previousState, state) || !isEqual(previousPathname, pathname)) {
      replaceLocationSearch();
    }
  }, [previousState, state, previousPathname, pathname, replaceLocationSearch]);
}

type QueryParametersFromSchema<T extends Record<string, z.ZodType<ValidQueryStringTypes>>> = {
  [Key in keyof T]: z.infer<T[Key]> | null;
};
function parseQueryParamsUsingSchema<
  TSchema extends Record<string, z.ZodType<ValidQueryStringTypes>>,
>(queryParameters: Record<string, string>, schema: TSchema): QueryParametersFromSchema<TSchema> {
  const queryParams = mapValues(schema, (zodType, key) => {
    const rawValue = queryParameters[key];
    if (rawValue == null) {
      return null;
    }
    const parsedValue = parseQueryValue(zodType, rawValue);

    const parsed = zodType.safeParse(parsedValue);
    if (!parsed.success) {
      return null;
    }
    return parsed.data;
  });
  return queryParams;
}

function parseQueryValue(zodType: z.ZodType<ValidQueryStringTypes>, value: string): unknown {
  if (zodType instanceof z.ZodArray) {
    if (value.length < 1) {
      return [];
    }
    return value.split(',').map(part => parseQueryValue(zodType.element, part));
  }
  if (zodType instanceof z.ZodString) {
    return value;
  }
  if (zodType instanceof z.ZodUnion) {
    // Try each union option until one succeeds
    for (const option of zodType.options) {
      const parsed = option.safeParse(parseQueryValue(option, value));
      if (parsed.success) {
        return parsed.data;
      }
    }
    return null;
  }
  if (zodType instanceof z.ZodNullable || zodType instanceof z.ZodOptional) {
    return parseQueryValue(zodType.unwrap(), value);
  }
  if (value === '') {
    return null;
  }
  if (zodType instanceof z.ZodNumber) {
    if (!isNaN(Number(value))) {
      return Number(value);
    }
    return null;
  }

  if (zodType instanceof z.ZodBoolean) {
    const isTrue = value.toLowerCase() === 'true',
      isFalse = value.toLowerCase() === 'false';
    if (isTrue || isFalse) {
      return isTrue;
    }
    return null;
  }

  return value;
}

function queryStringToDictionary(search: string): Record<string, string> {
  const query: Record<string, string> = {};
  const parts = (search[0] === '?' ? search.substr(1) : search).split('&');
  parts.forEach(part => {
    const [key, value] = part.split('=');
    const decodedValue = decodeURIComponent(value || '');
    query[decodeURIComponent(key)] = decodedValue;
  });
  return query;
}

function toQueryParameterDictionary(
  state: Record<string, ValidQueryStringTypes>
): Record<string, string | null> {
  return mapValues(state, value => {
    if (value == null) {
      return null;
    }
    if (Array.isArray(value)) {
      return value.map(value => (value == null ? 'null' : encodeURIComponent(value))).join(',');
    }
    return encodeURIComponent(value);
  });
}

function toQueryString(
  queryParameterDictionary: Record<string, string | null | undefined>
): string {
  return Object.entries(queryParameterDictionary)
    .map(([key, value]) => {
      if (String(key).length < 1 || value == null) {
        return null;
      }
      return `${key}=${value}`;
    })
    .filter(notNull)
    .join('&');
}
